import { Period } from '@heidi/commerce/contracts';
import { formatHeidi2UpgradeAmount } from '@heidi/commerce/data';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useIntl } from 'react-intl';
import { usePlanPriceDisplay } from '../components/pricing-plans-modal/use-plan-price-display';

const PLACEHOLDER = '—';

/**
 * Practice seat prices from the catalogue's yearly plans, formatted with the
 * same formatter as the upgrade preview so the plan card, the checkout and the
 * invoice breakdown always agree on the currency mark.
 */
export function usePracticeSeatPrices() {
  const { locale } = useIntl();
  const { plan: standardPlan } = usePlanPriceDisplay({
    tier: SubscriptionTier.CLINICIAN_PRO_PLUS,
    period: Period.ONE_YEAR,
  });
  const { plan: maxPlan } = usePlanPriceDisplay({
    tier: SubscriptionTier.CLINICIAN_PRO_PLUS,
    period: Period.ONE_YEAR,
    seatType: BillingSeatType.MAX,
  });

  const perUserMonth = (plan: typeof maxPlan) =>
    plan
      ? formatHeidi2UpgradeAmount(
          Math.round(plan.unit_amount / 12),
          plan.currency,
          locale,
          {
            trimZeroCents: true,
          }
        )
      : PLACEHOLDER;

  const perUserYear = (plan: typeof maxPlan) =>
    plan
      ? formatHeidi2UpgradeAmount(plan.unit_amount, plan.currency, locale)
      : PLACEHOLDER;

  const maxSeatYearlyUpcharge =
    maxPlan && standardPlan
      ? formatHeidi2UpgradeAmount(
          maxPlan.unit_amount - standardPlan.unit_amount,
          maxPlan.currency,
          locale,
          { trimZeroCents: true }
        )
      : PLACEHOLDER;

  return {
    standardSeatPrice: perUserMonth(standardPlan),
    maxSeatPrice: perUserMonth(maxPlan),
    standardSeatYearlyPrice: perUserYear(standardPlan),
    maxSeatYearlyPrice: perUserYear(maxPlan),
    maxSeatYearlyUpcharge,
    currency: maxPlan?.currency,
  };
}
